// Libraries
import * as React from 'react';
import { observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as classNames from 'classnames';

// Styles
const s = require('./Pagination.css');

// Types
type TypePaginationLinkType = 'page' | 'arrow' | 'ellipsis';

// Interfaces
interface IPaginationLink {
    key: string;
    page: number;
    text: string;
    type: TypePaginationLinkType;
    isActive?: boolean;
}

// Props
interface IPaginationProps {
    className?: string;
    entriesLoading?: boolean;
    entriesLoadingLabel?: string | null;
    entriesName?: string;
    noEntriesAvailableLabel?: string | null;
    edgesCount?: number;
    currentPage: number;
    countPerPage: number;
    countTotal: number;
    displayTotals?: boolean;
    onPageChange?: (newPage: number) => void;
}

@observer
export class Pagination extends React.Component<IPaginationProps, {}> {
    public static get defaultProps(): IPaginationProps {
        return {
            className: null,
            entriesLoading: false,
            entriesLoadingLabel: null,
            entriesName: 'entries',
            noEntriesAvailableLabel: null,
            edgesCount: 3,
            currentPage: 1,
            countPerPage: 20,
            countTotal: 0,
            displayTotals: true,
            onPageChange: null
        };
    }

    @observable public showingFrom: number = 1;
    @observable public showingTo: number = 1;
    @observable public currentPage: number = 1;
    @observable public totalPages: number = 1;
    @observable public pagesToDisplay: number[] = [1];
    @observable public displayEllipsisAtStart: boolean = false;
    @observable public displayEllipsisAtEnd: boolean = false;

    @computed private get linksToDisplay(): IPaginationLink[] {
        let links: IPaginationLink[] = [];

        if (this.displayEllipsisAtStart && typeof this.pagesToDisplay[0] !== 'undefined' && this.pagesToDisplay[0] > 1) {
            // Add first page
            links.push({
                key: 'edge-1',
                page: 1,
                text: '1',
                type: 'page'
            });

            // Add starting ellipsis
            const ellipsisAtStartPage = this.pagesToDisplay[0] - 1;
            links.push({
                key: 'ellipsis-' + ellipsisAtStartPage.toString(),
                page: ellipsisAtStartPage,
                text: '...',
                type: 'ellipsis'
            });
        }

        // Iterate over pages and add them to output
        this.pagesToDisplay.map((pageNo: number) => {
            links.push({
                key: 'page-' + pageNo.toString(),
                page: pageNo,
                text: pageNo.toString(),
                type: 'page',
                isActive: this.currentPage === pageNo
            });
        });

        if (this.pagesToDisplay.length > 0 && this.pagesToDisplay[this.pagesToDisplay.length - 1] < this.totalPages) {
            if (this.displayEllipsisAtEnd) {
                // Add ending ellipsis
                const ellipsisAtEndPage = this.pagesToDisplay[this.pagesToDisplay.length - 1] + 1;
                links.push({
                    key: 'ellipsis-' + ellipsisAtEndPage.toString(),
                    page: ellipsisAtEndPage,
                    text: '...',
                    type: 'ellipsis'
                });

                // Add last page
                links.push({
                    key: 'edge-' + this.totalPages.toString(),
                    page: this.totalPages,
                    text: this.totalPages.toString(),
                    type: 'page'
                });
            }
        }

        return links;
    }

    constructor(props, context) {
        super(props, context);

        // Set default state
        this.setShownPages(
            this.props.countPerPage,
            this.props.currentPage,
            this.calculatePagesCount(this.props.countTotal, this.props.countPerPage)
        );

        // Set pages to display
        this.setPagesToDisplay();
    }

    componentWillReceiveProps(nextProps) {
        // Check if total count or count per page has changed
        if (this.props.countTotal !== nextProps.countTotal || this.props.countPerPage !== nextProps.countPerPage) {
            // Calculate new total pages
            const totalPages = this.calculatePagesCount(nextProps.countTotal, nextProps.countPerPage);
            this.setPagesToDisplay(nextProps.currentPage, totalPages, nextProps.countTotal);
        } else if (this.props.currentPage !== nextProps.currentPage) {
            this.switchToPage(nextProps.currentPage);
        }
    }

    private calculatePagesCount(countTotal, countPerPage) {
        return Math.ceil(countTotal / countPerPage);
    }

    @action private setShownPages(showingTo: number, currentPage: number, totalPages: number) {
        runInAction(() => {
            this.showingTo = showingTo;
            this.currentPage = currentPage;
            this.totalPages = totalPages;
        });
    }

    @action private setPagesToDisplay(
        newPage?: number,
        newTotalPages?: number,
        newTotalCount?: number
    ) {
        // Get total pages
        const totalPages = typeof newTotalPages !== 'undefined' ? newTotalPages : this.totalPages;

        // Check if newPage is provided
        if (typeof newPage === 'undefined' || !newPage) {
            newPage = this.currentPage;
        }

        // Get total count
        const totalCount = typeof newTotalCount !== 'undefined' ? newTotalCount : this.props.countTotal;

        // Initially
        let displayEllipsisAtStart: boolean = false;
        let displayEllipsisAtEnd: boolean = false;
        let pagesToDisplay: number[] = [newPage];

        const totalEdgesToDisplay = this.props.edgesCount * 2;
        let countEdgesAdded = 0;

        if (totalEdgesToDisplay + 1 >= totalPages) {
            pagesToDisplay = [];
            for (let i = 1; i <= totalPages; i++) {
                pagesToDisplay.push(i);
                countEdgesAdded++;
            }
        } else {
            while (countEdgesAdded < totalEdgesToDisplay) {
                if (pagesToDisplay[0] > 1) {
                    pagesToDisplay.unshift(pagesToDisplay[0] - 1);
                    countEdgesAdded++;
                }

                if (pagesToDisplay[pagesToDisplay.length - 1] < totalPages) {
                    pagesToDisplay.push(pagesToDisplay[pagesToDisplay.length - 1] + 1);
                    countEdgesAdded++;
                }
            }
        }

        // Check if ellipsis should show at the start
        if (pagesToDisplay[0] > 2) {
            displayEllipsisAtStart = true;
        }

        // Check if ellipsis should show at the end
        if (pagesToDisplay[pagesToDisplay.length - 1] - 1 < totalPages) {
            displayEllipsisAtEnd = true;
        }

        // End of showing entries
        let showingTo: number = newPage * this.props.countPerPage;
        let showingFrom: number = showingTo - this.props.countPerPage + 1;
        showingTo = showingTo > totalCount ? totalCount : showingTo;

        // Set new state
        this.totalPages = totalPages;
        this.currentPage = newPage;
        this.pagesToDisplay = pagesToDisplay;
        this.showingFrom = showingFrom;
        this.showingTo = showingTo;
        this.displayEllipsisAtStart = displayEllipsisAtStart;
        this.displayEllipsisAtEnd = displayEllipsisAtEnd;
    }

    @action private switchToPage = (newPage) => (e) => {
        // Check if new page is smaller than first page
        if (newPage < 1) {
            newPage = this.totalPages;
        } else if (newPage > this.totalPages) {
            newPage = 1;
        }

        // Change current page
        this.setPagesToDisplay(newPage);

        // Pass event further
        if (this.props.onPageChange) {
            this.props.onPageChange(newPage);
        }
    }

    public render() {
        // Pagination class name
        let paginationClassName = classNames(s.container, this.props.className);

        // If no results
        if (this.props.entriesLoading === false && this.props.countTotal === 0) {
            return (
                <PaginationEmpty
                    className={paginationClassName}
                    entriesName={this.props.entriesName}
                    noEntriesAvailableLabel={this.props.noEntriesAvailableLabel}
                />
            );
        } else {
            // Prepare pagination links
            const links = (
                <div className={s.flex}>
                    {(this.linksToDisplay && this.linksToDisplay.length > 0) && (
                        <ul>
                            {this.linksToDisplay.map((link) => {
                                return (
                                    <PaginationLink
                                        key={link.key}
                                        type={link.type}
                                        text={link.text}
                                        onSwitch={this.switchToPage}
                                        switchToPage={link.page}
                                        isActive={typeof link.isActive !== 'undefined' ? link.isActive : false}
                                    />
                                );
                            })}
                        </ul>
                    )}
                </div>
            );

            if (this.props.entriesLoading) {
                return (
                    <div className={paginationClassName}>
                        <div className={s.flex}>
                            <PaginationLoading
                                entriesName={this.props.entriesName}
                                entriesLoadingLabel={this.props.entriesLoadingLabel}
                            />
                            {links}
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className={paginationClassName}>
                        <div className={s.flex}>
                            <PaginationTotals
                                entriesName={this.props.entriesName}
                                displayTotals={this.props.displayTotals}
                                countTotal={this.props.countTotal}
                                showingFrom={this.showingFrom}
                                showingTo={this.showingTo}
                            />
                            {links}
                        </div>
                    </div>
                );
            }
        }
    }
}

const paginationEmptyStateless: React.StatelessComponent<{
    className: string;
    entriesName: string;
    noEntriesAvailableLabel: string | null;
}> = (props) => {
    const label = props.noEntriesAvailableLabel !== null
        ? props.noEntriesAvailableLabel
        : `No ${props.entriesName} available`;
    return (
        <div className={props.className}>
            <div className={classNames(s.flex, s.flexShrink)}>
                <p className={s.totals}>{label}</p>
            </div>
        </div>
    );
};
// tslint:disable-next-line:variable-name
const PaginationEmpty = observer(paginationEmptyStateless);

const paginationLoadingStateless: React.StatelessComponent<{
    entriesName: string;
    entriesLoadingLabel: string | null;
}> = (props) => {
    const label = props.entriesLoadingLabel !== null
        ? props.entriesLoadingLabel
        : `Loading ${props.entriesName}...`;
    return (
        <div className={classNames(s.flex, s.flexShrink)}>
            <p className={s.totals}>{label}</p>
        </div>
    );
};
// tslint:disable-next-line:variable-name
const PaginationLoading = observer(paginationLoadingStateless);

const paginationTotalsStateless: React.StatelessComponent<{
    entriesName: string;
    displayTotals: boolean;
    showingFrom: number;
    showingTo: number;
    countTotal: number;
}> = (props) => {
    if (props.displayTotals) {
        return (
            <div className={s.flex}>
                <p className={s.totals}>{`Showing ${props.entriesName}`}&nbsp;
                    <strong>{props.showingFrom.toString()}</strong>&nbsp;&mdash;&nbsp;
                    <strong>{props.showingTo.toString()}</strong>&nbsp;from total of&nbsp;
                    <strong>{props.countTotal.toString()}</strong>
                </p>
            </div>
        );
    } else {
        return null;
    }
};
// tslint:disable-next-line:variable-name
const PaginationTotals = observer(paginationTotalsStateless);

const paginationLinkStateless: React.StatelessComponent<{
    type: TypePaginationLinkType;
    switchToPage: number;
    text: string;
    isActive: boolean;
    onSwitch: (page) => any;
}> = (props) => {
    // Class name
    let paginationClassName;
    switch (props.type) {
        case 'arrow':
            paginationClassName = s.linkArrow;
            break;

        case 'ellipsis':
            paginationClassName = s.linkEllipsis;
            break;

        default:
            paginationClassName = classNames(s.linkPage, {
                [s.linkPageActive]: props.isActive
            });
            break;
    }

    // Render
    return (
        <li className={paginationClassName}>
            <button onClick={props.onSwitch(props.switchToPage)}>
                {props.text}
            </button>
        </li>
    );
};
// tslint:disable-next-line:variable-name
const PaginationLink = observer(paginationLinkStateless);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/pagination/Pagination.tsx